import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, ChevronLeft, Plus, Pencil, UserX, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { usersApi } from '../../lib/api/usersApi';
import UserDialog from './UserDialog';

const OrganizationUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const fetchUsers = () => {
    setLoading(true);
    usersApi
      .list()
      .then((data) => setUsers(data.users || []))
      .catch(() => {
        toast.error('Failed to load users');
        setUsers([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEdit = (u) => {
    setEditingUser(u);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.name || u.email}? They will no longer be able to sign in.`))
      return;
    try {
      await usersApi.update(u._id, { isActive: false });
      toast.success('User deactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate');
    }
  };

  const roleLabel = (r) => {
    if (r === 'admin') return 'Org Admin';
    if (r === 'department_admin' || r === 'dept_admin') return 'Team Admin';
    return 'User';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Link
        to="/dashboard/organization"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 mb-6"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Organization
      </Link>
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Users</h1>
              <p className="text-sm text-gray-500">
                Invite members and manage roles and permissions.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setEditingUser(null);
              setDialogOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm font-medium text-violet-700 shadow-sm hover:bg-violet-100 transition"
          >
            <Plus className="w-4 h-4" />
            New User
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 mb-4">
              <Users className="w-7 h-7" />
            </div>
            <p className="text-gray-600 font-medium">No users yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Invite team members; they’ll receive an email to set their password and sign in.
            </p>
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-700"
            >
              <Plus className="w-4 h-4" />
              New User
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Teams
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u._id}
                    className={`border-b border-gray-50 hover:bg-gray-50/50 ${
                      u.isActive === false ? 'opacity-60' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">{u.name}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-800">
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {(u.departmentIds || []).map((d) => d.name || d).join(', ') || '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(u)}
                          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                          aria-label="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {u.isActive !== false && (
                          <button
                            type="button"
                            onClick={() => handleDeactivate(u)}
                            className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600"
                            aria-label="Deactivate"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UserDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSuccess={fetchUsers}
        user={editingUser}
      />
    </div>
  );
};

export default OrganizationUsers;
